import { useEffect, useState } from 'react';
import {
    Box, Button, TextField, Typography, Avatar, RadioGroup, Radio, FormControlLabel, FormControl, FormLabel, CircularProgress, Alert, Link as MuiLink
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors, loadUser, updateProfile } from '../../actions/userAction';
import { UPDATE_PROFILE_RESET } from '../../constants/userConstants';
import MetaData from '../Layouts/MetaData';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const UpdateProfile = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.user);
    const { error, isUpdated, loading } = useSelector((state) => state.profile);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("");
    const [avatar, setAvatar] = useState("");
    const [avatarPreview, setAvatarPreview] = useState("");

    const updateProfileHandler = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.set("name", name);
        formData.set("email", email);
        formData.set("gender", gender);
        formData.set("avatar", avatar);

        dispatch(updateProfile(formData));
    };

    const handleUpdateDataChange = (e) => {
        const reader = new FileReader();
        setAvatar("");
        reader.onload = () => {
            if (reader.readyState === 2) {
                setAvatarPreview(reader.result);
                setAvatar(reader.result);
            }
        };

        reader.readAsDataURL(e.target.files[0]);
    };

    useEffect(() => {
        if (user) {
            setName(user.name);
            setEmail(user.email);
            setGender(user.gender);
            if (user.avatar && user.avatar.url) {
                setAvatarPreview(user.avatar.url);
            }
        }
        if (error) {
            console.error(error);
            dispatch(clearErrors());
        }
        if (isUpdated) {
            dispatch(loadUser());
            navigate('/account');
            dispatch({ type: UPDATE_PROFILE_RESET });
        }
    }, [dispatch, error, user, isUpdated, navigate]);

    return (
        <>
            <MetaData title="Update Profile - AmritLok" />
            <Box
                component="main"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 128px)',
                    p: { xs: 2, sm: 3 },
                    backgroundColor: 'background.default',
                }}
            >
                <MetaData title="Update Profile - AmritLok" />
                <Typography component="h1" variant="h4" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
                    Update Profile
                </Typography>

                <Box
                    component="form"
                    onSubmit={updateProfileHandler}
                    noValidate
                    sx={{
                        mt: 2,
                        p: { xs: 3, sm: 4 },
                        borderRadius: 2,
                        boxShadow: 5,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: { xs: '100%', sm: '80%', md: '60%' },
                    }}
                >
                    {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Full Name"
                        name="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        label="Email Address"
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                    <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                        <FormLabel component="legend" sx={{ textAlign: 'left', width: '100%', mb: 1 }}>Gender</FormLabel>
                        <RadioGroup
                            row
                            aria-label="gender"
                            name="gender"
                            value={gender}
                            onChange={(e) => setGender(e.target.value)}
                        >
                            <FormControlLabel value="male" control={<Radio />} label="Male" />
                            <FormControlLabel value="female" control={<Radio />} label="Female" />
                        </RadioGroup>
                    </FormControl>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: 2,
                            mb: 2,
                        }}
                    >
                        <Avatar
                            src={avatarPreview}
                            sx={{
                                width: 56,
                                height: 56,
                                mr: 2,
                                transition: 'transform 0.2s ease-in-out',
                                '&:hover': { transform: 'scale(1.1)' },
                            }}
                        />
                        <Button
                            component="label"
                            role={undefined}
                            variant="contained"
                            tabIndex={-1}
                            sx={{ transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                        >
                            Upload Avatar
                            <VisuallyHiddenInput type="file" onChange={handleUpdateDataChange} name="avatar" accept="image/*" />
                        </Button>
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{ mt: 3, mb: 2, py: 1.5, transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Update'}
                    </Button>

                    <MuiLink component={Link} to="/account" sx={{ alignSelf: 'flex-end' }}>
                        Cancel
                    </MuiLink>
                </Box>
            </Box>
        </>
    );
};

export default UpdateProfile;